'use client';

import { Eye } from 'lucide-react';
import Link from 'next/link';
import type { CatalogItem, CatalogType } from '@/lib/services/catalog/catalog-types';
import Image from 'next/image';

interface CatalogCardProps {
  artwork: CatalogItem;
  onView?: (artwork: CatalogItem) => void;
  onEnlarge: (artwork: CatalogItem) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onCopyWallet?: (walletAddress: string | undefined) => void;
  copiedWallet?: string | null;
  catalogType?: CatalogType;
  galleryWall?: boolean;
  index?: number;
}

// Function to get a color for a tag based on its string value
const getTagColor = (tag: string): string => {
  // Create a simple hash function for the tag string
  const hash = tag.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  // Define a set of colors to choose from
  const colors = [
    // Gri tonları
    // { bg: "bg-zinc-100", text: "text-zinc-700" },
    // { bg: "bg-zinc-200", text: "text-zinc-800" },
    // { bg: "bg-gray-100", text: "text-gray-700" },
    // { bg: "bg-gray-200", text: "text-gray-800" },
    // { bg: "bg-stone-100", text: "text-stone-700" },
    // { bg: "bg-stone-200", text: "text-stone-800" },
    // { bg: "bg-slate-100", text: "text-slate-700" },
    // { bg: "bg-slate-200", text: "text-slate-800" },

    // Mavi tonları
    { bg: 'bg-blue-50', text: 'text-blue-600' },
    { bg: 'bg-blue-100', text: 'text-blue-700' },
    { bg: 'bg-sky-50', text: 'text-sky-600' },
    { bg: 'bg-sky-100', text: 'text-sky-700' },
    { bg: 'bg-indigo-50', text: 'text-indigo-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700' },

    // Yeşil tonları
    { bg: 'bg-green-50', text: 'text-green-600' },
    { bg: 'bg-green-100', text: 'text-green-700' },
    { bg: 'bg-emerald-50', text: 'text-emerald-600' },
    { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    { bg: 'bg-teal-50', text: 'text-teal-600' },
    { bg: 'bg-teal-100', text: 'text-teal-700' },

    // Sarı/Turuncu tonları
    { bg: 'bg-yellow-50', text: 'text-yellow-600' },
    { bg: 'bg-yellow-100', text: 'text-yellow-700' },
    { bg: 'bg-amber-50', text: 'text-amber-600' },
    { bg: 'bg-amber-100', text: 'text-amber-700' },
    { bg: 'bg-orange-50', text: 'text-orange-600' },
    { bg: 'bg-orange-100', text: 'text-orange-700' },

    // Kırmızı/Pembe tonları
    { bg: 'bg-red-50', text: 'text-red-600' },
    { bg: 'bg-red-100', text: 'text-red-700' },
    { bg: 'bg-rose-50', text: 'text-rose-600' },
    { bg: 'bg-rose-100', text: 'text-rose-700' },
    { bg: 'bg-pink-50', text: 'text-pink-600' },
    { bg: 'bg-pink-100', text: 'text-pink-700' },

    // Mor tonları
    { bg: 'bg-purple-50', text: 'text-purple-600' },
    { bg: 'bg-purple-100', text: 'text-purple-700' },
    { bg: 'bg-violet-50', text: 'text-violet-600' },
    { bg: 'bg-violet-100', text: 'text-violet-700' },
    { bg: 'bg-fuchsia-50', text: 'text-fuchsia-600' },
    { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  ];

  // Use the hash to pick a color
  const index = Math.abs(hash) % colors.length;
  return `${colors[index].bg} ${colors[index].text}`;
};

export function CatalogCard({
  artwork,
  onView,
  onEnlarge,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  onCopyWallet,
  copiedWallet,
  catalogType = 'all',
  galleryWall = false,
  index = 0,
}: CatalogCardProps) {
  // Sabit değerler
  let artworkCount = 0;
  let collectorCount = 0;
  let showCollectionStats = false;

  switch (artwork.type) {
    case 'collection':
      artworkCount = artwork.artworkCount || 0;
      collectorCount = artwork.collectorCount || 0;
      showCollectionStats = true;
      break;
  }

  const additionalDivClass: React.CSSProperties = {};
  const additionalArtWorkClass: React.CSSProperties = {};
  if (galleryWall) {
    additionalDivClass['height'] = `calc(100dvh - 215px)`;
    additionalDivClass['minHeight'] = `calc(100dvh - 215px)`;
    additionalDivClass['minWidth'] = `250px`;
    additionalDivClass['width'] = `auto`;
    additionalDivClass['position'] = `relative`;
    additionalDivClass['scrollSnapAlign'] = 'center'; // Make all images snap to center
    additionalDivClass['scrollSnapStop'] = 'always'; // Ensure it always stops at each snap point
    additionalDivClass['paddingBottom'] = 'none';
    additionalArtWorkClass['height'] = `calc(100dvh - 215px)`;
    additionalArtWorkClass['minHeight'] = `calc(100dvh - 215px)`;
    additionalArtWorkClass['display'] = 'block';
    additionalArtWorkClass['width'] = 'auto';
    additionalArtWorkClass['maxWidth'] = 'none';
    additionalArtWorkClass['position'] = 'relative';
  }

  return (
    <Link
      href={artwork.href || '#'}
      className={galleryWall ? 'block cursor-pointer flex-none h-full' : 'block cursor-pointer'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
    >
      <div
        className="masonry-item rounded-md overflow-hidden group bg-white"
        style={galleryWall ? { height: `calc(100dvh - 140px)`, minHeight: `calc(100dvh - 140px)` } : {}}
      >
        <div
          className="overflow-hidden relative"
          style={{
            position: 'relative',
            paddingBottom: artwork.width && artwork.height ? `${(artwork.height / artwork.width) * 100}%` : '100%',
            height: '200px',
            maxHeight: '200px',
            ...additionalDivClass,
          }}
        >
          <Image
            src={artwork.thumbnail}
            alt={artwork.title}
            className="top-0 left-0 w-full h-full object-cover rounded-md"
            loading="lazy"
            width={artwork.width}
            height={artwork.height}
            style={{
              position: 'absolute',
              objectPosition: 'center center',
              ...additionalArtWorkClass,
            }}
          />
          {/* Katalog tipi etiketi */}
          {/* {typeTag.label && (
              <div className={`absolute top-2 right-2 ${typeTag.bgColor} ${typeTag.textColor} text-xs px-2 py-1 font-mono rounded-sm`}>
                {typeTag.label}
              </div>
            )} */}
          {artwork?.tags && artwork.tags.includes('UNFILTERED') && (
            <div className="absolute top-2 left-2 bg-zinc-200 text-zinc-800 text-xs px-2 py-1 font-mono">
              UNFILTERED
            </div>
          )}
        </div>
        <div className="p-0 mt-[4px]">
          <div className="flex justify-between items-start">
            <p className="text-[10px] font-mono text-zinc-800 uppercase">{artwork.title}</p>
            <div className="flex items-center gap-1">
              {/*
              æ
              <button
                className="w-5 h-5 flex items-center justify-center transition-all text-zinc-500 hover:text-zinc-800 -mt-[3px] -mr-[4px]"
                title="Enlarge artwork"
                onClick={e => {
                  e.stopPropagation();
                  onEnlarge(artwork);
                }}
              >
                <Maximize2 className="h-3 w-3 text-zinc-700" />
              </button>
             
              {onView && (
                <button
                  className="w-5 h-5 flex items-center justify-center transition-all text-zinc-500 hover:text-zinc-800 -mt-[3px] -mr-[4px]"
                  title="View details"
                  onClick={e => {
                    e.stopPropagation();
                    onView(artwork);
                  }}
                >
                  <Eye className="h-3 w-3 text-zinc-700" />
                </button>
              )}
              */}
              <button
                className="w-5 h-5 flex items-center justify-center transition-all text-zinc-500 hover:text-zinc-800 -mt-[3px] -mr-[4px]"
                title="View details"
                onClick={e => {
                  e.stopPropagation();
                  onView(artwork);
                }}
              >
                <Eye className="h-3 w-3 text-zinc-700" />
              </button>
            </div>
          </div>

          {/* Collection Statistics - Only show for collection type on client side */}
          {showCollectionStats && (
            <div className="flex flex-wrap gap-1 mt-1">
              {artworkCount > 0 && (
                <span className="text-[8px] px-1 py-0.5 font-mono bg-zinc-200 text-zinc-700 rounded-sm">
                  {artworkCount} ARTWORKS
                </span>
              )}
              {collectorCount > 0 && (
                <span className="text-[8px] px-1 py-0.5 font-mono bg-zinc-200 text-zinc-700 rounded-sm">
                  {collectorCount} COLLECTORS
                </span>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-1 mt-1">
            {artwork.tags && artwork.tags.map(tag => (
              <span key={tag} className={`text-[8px] px-1 py-0.5 font-mono ${getTagColor(tag)}`}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}
